import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { forwardRef, useMemo, useEffect } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { MenuProvider, useMenu } from './Menu.context.js';
import { MenuList } from './MenuList.js';
import { bordersTemplate, useToken, mergeRefs, useIsomorphicLayoutEffect, useIsInDialog, useGlobalKeyPress } from '@equinor/eds-utils';
import { menu } from './Menu.tokens.js';
import { offset, flip, shift, size, useFloating, autoUpdate, useInteractions, useDismiss, FloatingPortal } from '@floating-ui/react';
import { jsx, Fragment } from 'react/jsx-runtime';
import { Paper } from '../Paper/Paper.js';
import { useEds } from '../EdsProvider/eds.context.js';

var _excluded = ["children", "anchorEl", "onClose", "open"],
  _excluded2 = ["anchorEl", "open", "placement", "matchAnchorWidth", "onClose", "style", "className"];
var border = menu.border;
var MenuPaper = styled(Paper).withConfig({
  displayName: "Menu__MenuPaper",
  componentId: "sc-1vlnqcj-0"
})(["position:absolute;z-index:1400;width:fit-content;min-width:fit-content;", ";", ";"], bordersTemplate(border), function (_ref) {
  var open = _ref.open;
  return css({
    display: open ? 'block' : 'none'
  });
});
var MenuContainer = /*#__PURE__*/forwardRef(function MenuContainer(_ref2, ref) {
  var children = _ref2.children,
    anchorEl = _ref2.anchorEl,
    onCloseCallback = _ref2.onClose,
    open = _ref2.open,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var _useMenu = useMenu(),
    setOnClose = _useMenu.setOnClose,
    onClose = _useMenu.onClose,
    setInitialFocus = _useMenu.setInitialFocus,
    focusedIndex = _useMenu.focusedIndex;
  var closeMenuOnClickIndexes = useMemo(function () {
    return [];
  }, []);
  useEffect(function () {
    if (onClose === null && onCloseCallback) {
      setOnClose(onCloseCallback);
    }
  }, [onClose, onCloseCallback, setOnClose]);
  useEffect(function () {
    var openWithKey = function openWithKey(e) {
      var key = e.key;
      //activate menu with arrows according to wai-aria best practices
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        anchorEl.dispatchEvent(new Event('click', {
          bubbles: true
        }));
      }
      switch (key) {
        case 'Enter':
        case 'ArrowDown':
          setInitialFocus('first');
          break;
        case 'ArrowUp':
          setInitialFocus('last');
          break;
      }
    };
    if (anchorEl) anchorEl.addEventListener('keydown', openWithKey);
    return function () {
      if (anchorEl) anchorEl.removeEventListener('keydown', openWithKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);
  useGlobalKeyPress('Escape', function () {
    if (open && onClose !== null) {
      onClose();
      anchorEl.focus();
    }
  });
  useGlobalKeyPress('Enter', function () {
    if (open && onClose !== null && closeMenuOnClickIndexes.includes(focusedIndex)) {
      setTimeout(function () {
        if (window.document.contains(anchorEl)) {
          anchorEl.focus();
        }
      }, 0);
    }
  });
  var addCloseMenuOnClickIndex = function addCloseMenuOnClickIndex(index) {
    if (closeMenuOnClickIndexes.indexOf(index) < 0) {
      closeMenuOnClickIndexes.push(index);
    }
  };
  return /*#__PURE__*/jsx(MenuList, _objectSpread(_objectSpread({
    addCloseMenuOnClickIndex: addCloseMenuOnClickIndex
  }, rest), {}, {
    ref: ref,
    children: children
  }));
});
var Menu = /*#__PURE__*/forwardRef(function Menu(_ref3, ref) {
  var anchorEl = _ref3.anchorEl,
    open = _ref3.open,
    _ref3$placement = _ref3.placement,
    placement = _ref3$placement === void 0 ? 'bottom' : _ref3$placement,
    _ref3$matchAnchorWidt = _ref3.matchAnchorWidth,
    matchAnchorWidth = _ref3$matchAnchorWidt === void 0 ? false : _ref3$matchAnchorWidt,
    onClose = _ref3.onClose,
    style = _ref3.style,
    className = _ref3.className,
    rest = _objectWithoutProperties(_ref3, _excluded2);
  var _useEds = useEds(),
    density = _useEds.density;
  var token = useToken({
    density: density
  }, menu);
  var floatingMiddleware = [offset(4), flip(), shift({
    padding: 8
  })];
  if (matchAnchorWidth) {
    floatingMiddleware = [].concat(_toConsumableArray(floatingMiddleware), [size({
      apply: function apply(_ref4) {
        var rects = _ref4.rects,
          elements = _ref4.elements;
        Object.assign(elements.floating.style, {
          width: "".concat(rects.reference.width, "px")
        });
      }
    })]);
  }
  var _useFloating = useFloating({
      elements: {
        reference: anchorEl
      },
      placement: placement,
      open: open,
      onOpenChange: onClose,
      middleware: floatingMiddleware
    }),
    x = _useFloating.x,
    y = _useFloating.y,
    refs = _useFloating.refs,
    update = _useFloating.update,
    strategy = _useFloating.strategy,
    context = _useFloating.context;
  var popoverRef = useMemo(function () {
    return mergeRefs(refs.setFloating, ref);
  }, [refs.setFloating, ref]);
  useIsomorphicLayoutEffect(function () {
    if (refs.reference.current && refs.floating.current && open) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
  }, [refs.reference, refs.floating, update, open]);
  var _useInteractions = useInteractions([useDismiss(context, {
      escapeKey: false
    })]),
    getFloatingProps = _useInteractions.getFloatingProps;
  var props = {
    open: open,
    className: className
  };
  var menuProps = _objectSpread(_objectSpread({}, rest), {}, {
    onClose: onClose,
    anchorEl: anchorEl,
    open: open
  });

  //temporary fix when inside dialog. Should be replaced by popover api when it is ready
  var inDialog = useIsInDialog(anchorEl);
  var menuElement = /*#__PURE__*/jsx(ThemeProvider, {
    theme: token,
    children: /*#__PURE__*/jsx(MenuPaper, _objectSpread(_objectSpread(_objectSpread({
      elevation: "raised"
    }, props), getFloatingProps({
      ref: popoverRef,
      style: _objectSpread(_objectSpread({}, style), {}, {
        position: strategy,
        top: y || 0,
        left: x || 0
      })
    })), {}, {
      children: /*#__PURE__*/jsx(MenuProvider, {
        children: /*#__PURE__*/jsx(MenuContainer, _objectSpread(_objectSpread({}, menuProps), {}, {
          ref: ref
        }))
      })
    }))
  });
  return /*#__PURE__*/jsx(Fragment, {
    children: inDialog ? menuElement : /*#__PURE__*/jsx(FloatingPortal, {
      id: "eds-menu-container",
      children: menuElement
    })
  });
});

export { Menu };
